import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { Check, LifeBuoy } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Hero } from '@/design';
import type { IMyCycleResponse } from '../api/contracts';
import { formatDay } from '../format';
import { PORTAL_PATHS, receiptPath, responseFormPath } from '../paths';
import { surveyTitle } from '../survey-labels';
import {
  AddSurveyCard,
  ResponseRow,
  emptyDashboard,
  responsesOf,
  useCanAdd,
  useMyCatalogue,
  type ICycleResponse
} from './catalogue';
import { IdentifierConfirmations } from './identifier-confirmation-card';
import { StartSurveyCard, isStartable } from './start-survey-card';

/**
 * The Advisor Portal's Home — what this cycle asks of the account, and one clear way to carry on
 * (the two-portals design, §3.1).
 *
 * It reads exactly what My surveys reads (`useMyCatalogue`) and draws the active cycles' half of
 * it: the responses under way, the start affordance for what is open and not started, and the
 * identifier cards. Past cycles stay on My surveys, which a line under the list points to.
 *
 * **The hero states what the catalogue says, and derives nothing.** Its one button continues the
 * first draft in an active cycle, in the API's own order; its deadline is that cycle's `closesAt`
 * as sent. With no draft it says how many cycles are open to start, or that this cycle's surveys
 * are in, or — for an account entitled to nothing — the catalogue's own empty sentence, word for
 * word, so the two screens cannot describe one state two ways. Over a catalogue that could not
 * be read it says nothing of the kind: the failure is the page's banner, and the hero stays the
 * neutral one the loading state draws.
 */
export function HomePage() {
  const navigate = useNavigate();
  // "Finish later" comes here with the word that its answers are saved. Said once: the entry's
  // state drops it, or Back and a reload would say it again after answers nobody saved.
  const location = useLocation();
  const [saved] = React.useState(
    () => (location.state as Nullable<{ saved?: boolean }>)?.saved === true
  );
  React.useEffect(() => {
    if (saved) navigate(location, { replace: true, state: null });
    // Once, on arrival: run again on the location it replaces, it would replace it again.
  }, []);
  const { cycles, participants, identifiers, error, setError, answered, awaiting } =
    useMyCatalogue();
  const canAdd = useCanAdd();

  const open = (response: IMyCycleResponse) =>
    navigate(
      response.status === 'submitted'
        ? receiptPath(response.responseId)
        : responseFormPath(response.responseId)
    );

  const loaded = cycles ?? [];
  const active = loaded.filter((cycle) => cycle.phase === 'active');
  const startable = active.filter(isStartable);
  const current = responsesOf(loaded, participants).filter(
    (row) => row.cycle.phase === 'active'
  );
  const next = current.find((row) => row.response.status === 'draft');
  // A response set aside — archived beside its live replacement — is neither answered nor
  // waiting to be, so it decides nothing here; what is left has to be in.
  const counted = current.filter((row) => !SET_ASIDE.includes(row.response.status));
  const answeredAll =
    counted.length > 0 && counted.every((row) => ANSWERED.includes(row.response.status));
  const empty = emptyDashboard(loaded, startable);
  const hasPast = loaded.some((cycle) => cycle.phase === 'historical' && cycle.responses.length > 0);

  return (
    <>
      {/* The neutral hero while the catalogue is on its way, and over one that could not be
          read: `useMyCatalogue` hands a failed read on as `[]`, and the empty catalogue's
          sentence is a claim about the account that the failure cannot back — My surveys
          withholds its empty card on the same condition. */}
      {cycles === null || (error !== null && loaded.length === 0) ? (
        <Hero documentTitle="Home" title="Your surveys" />
      ) : (
        <HomeHero
          next={next}
          startable={startable.length}
          answeredAll={answeredAll}
          empty={empty}
          cycleCode={active[0]?.cycle.code}
        />
      )}

      <div className="mx-auto grid w-full max-w-[1120px] gap-10 px-6 pt-10 pb-16 lg:grid-cols-[minmax(0,1fr)_20rem] lg:px-10">
        <section aria-labelledby="home-asks" className="grid content-start gap-4">
          {saved && (
            <p
              role="status"
              className="flex items-center gap-1.5 text-sm text-[var(--color-teal)]"
            >
              <Check className="size-4" aria-hidden />
              Your answers are saved.
            </p>
          )}
          <h2
            id="home-asks"
            className="text-[1.375rem] font-normal text-[var(--color-royal-blue)]"
          >
            Asked of you this cycle
          </h2>

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {cycles === null ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              {current.length === 0 && startable.length === 0 && error === null && (
                <Card>
                  <CardHeader>
                    <CardTitle>{empty.title}</CardTitle>
                    <CardDescription>{empty.body}</CardDescription>
                  </CardHeader>
                </Card>
              )}

              {current.map((row) => (
                <ResponseRow key={row.response.responseId} row={row} onOpen={open} />
              ))}

              <StartSurveyCard
                cycles={startable}
                waiting={awaiting}
                canAdd={canAdd}
                participants={participants}
                onOpened={(responseId) => navigate(responseFormPath(responseId))}
                onError={setError}
              />

              {hasPast && (
                <p className="text-[0.9375rem] text-muted-foreground">
                  Your earlier cycles are in{' '}
                  <Link
                    to={PORTAL_PATHS.surveys}
                    className="text-[var(--color-sapphire)] underline underline-offset-4"
                  >
                    My surveys
                  </Link>
                  .
                </p>
              )}
            </>
          )}
        </section>

        <aside className="grid content-start gap-5">
          {cycles !== null && (
            <IdentifierConfirmations
              identifiers={identifiers}
              participants={participants}
              onAnswered={answered}
            />
          )}
          {/* The way the list grows (the teams onboarding plan's R7), beside it rather than in
              it: Home is this cycle's asks, and "Add a survey" is about the account. */}
          {canAdd && <AddSurveyCard />}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-medium">
                <LifeBuoy className="size-4 text-[var(--color-sapphire)]" aria-hidden />
                Need a hand?
              </CardTitle>
              <CardDescription>
                What to do if a survey or a number is not what you expect, and what to quote.
              </CardDescription>
              <div className="pt-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={PORTAL_PATHS.help}>Open Help</Link>
                </Button>
              </div>
            </CardHeader>
          </Card>
        </aside>
      </div>
    </>
  );
}

/** The statuses of a response that is in — submitted, or locked against further change. */
const ANSWERED: readonly string[] = ['submitted', 'locked'];
/** The statuses of a response no longer in play: its cycle's answer is another row's, or none.
 *  survey-api sends a respondent no recycled response (its recycle bin design D6); the status
 *  stays here, as a status this page was not sent must still be read right. */
const SET_ASIDE: readonly string[] = ['recycled', 'archived'];

function HomeHero({
  next,
  startable,
  answeredAll,
  empty,
  cycleCode
}: {
  next: ICycleResponse | undefined;
  startable: number;
  answeredAll: boolean;
  empty: { title: string; body: string };
  cycleCode: string | undefined;
}) {
  const context = cycleCode ? `${cycleCode} ranking cycle` : undefined;

  if (next) {
    const title = surveyTitle(next.cycle.survey.code);
    const closes = next.cycle.closesAt;
    return (
      <Hero
        documentTitle="Home"
        context={context}
        title={closes ? `Your ${title} survey is open until ${formatDay(closes)}` : `Your ${title} survey is open`}
        lede="It opens where you stopped, and everything you enter is saved as you go."
        action={
          <Button
            asChild
            size="lg"
            className="bg-white text-[var(--color-sapphire)] hover:bg-[var(--color-slight-blue)]"
          >
            <Link to={responseFormPath(next.response.responseId)}>Continue the {title} survey</Link>
          </Button>
        }
      />
    );
  }

  if (startable > 0) {
    return (
      <Hero
        documentTitle="Home"
        context={context}
        title={startable === 1 ? 'A survey is ready for you to start' : `${startable} surveys are ready for you to start`}
        lede="Each one saves as you go, so you can stop and come back whenever you like."
      />
    );
  }

  if (answeredAll) {
    return (
      <Hero
        documentTitle="Home"
        context={context}
        title="Your surveys for this cycle are in"
        lede="You can still correct a submitted survey until its cycle closes."
      />
    );
  }

  return <Hero documentTitle="Home" context={context} title={empty.title} lede={empty.body} />;
}
